import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { 
  Plus, 
  Music, 
  Globe, 
  Lock, 
  User,
  Loader2,
  ListMusic
} from 'lucide-react';
import { StageEmptyState } from '../components/StageEmptyState';
import { API } from '../config/api';



export default function PlaylistsPage() {
  const navigate = useNavigate();
  const { getAuthHeaders, isAuthenticated } = useAuth();
  
  const [playlists, setPlaylists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newPlaylist, setNewPlaylist] = useState({
    name: '',
    description: '',
    is_public: true
  });

  useEffect(() => {
    fetchPlaylists();
  }, []);

  const fetchPlaylists = async () => {
    try {
      const headers = isAuthenticated ? getAuthHeaders() : {};
      const response = await fetch(`${API}/playlists`, { headers });
      
      if (response.ok) {
        const data = await response.json();
        setPlaylists(data.playlists || []);
      }
    } catch (error) {
      console.error('Error fetching playlists:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePlaylist = async () => {
    if (!newPlaylist.name.trim()) {
      toast.error('Playlist name is required');
      return;
    }

    setCreating(true);
    try {
      const response = await fetch(`${API}/playlists`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify(newPlaylist)
      });

      if (response.ok) {
        const created = await response.json();
        toast.success('Playlist created!');
        setShowCreateModal(false);
        setNewPlaylist({ name: '', description: '', is_public: true });
        navigate(`/playlists/${created.playlist_id}`);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create playlist');
      }
    } catch (error) {
      toast.error('Failed to create playlist');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="min-h-screen py-8 px-4" data-testid="playlists-page">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              <ListMusic className="w-8 h-8 text-primary" />
              Playlists
            </h1>
            <p className="text-muted-foreground mt-1">Create and manage your music collections</p>
          </div>
          
          {isAuthenticated && (
            <Button onClick={() => setShowCreateModal(true)} data-testid="create-playlist-btn">
              <Plus className="w-4 h-4 mr-2" />
              Create Playlist
            </Button>
          )}
        </div>

        {/* Playlists Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : playlists.length === 0 ? (
          <StageEmptyState
            variant="default"
            customHeadline="No playlists yet"
            customHint={isAuthenticated ? "Create your first playlist to start organizing your favorite tracks!" : "Sign in to create playlists"}
            customAction={isAuthenticated ? (
              <Button onClick={() => setShowCreateModal(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Create Playlist
              </Button>
            ) : null}
          />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {playlists.map((playlist) => (
              <Link key={playlist.playlist_id} to={`/playlists/${playlist.playlist_id}`}>
                <Card className="p-4 bg-card/50 border border-border hover:border-primary/50 transition-all hover:scale-[1.02] cursor-pointer group">
                  {/* Cover */}
                  <div className="aspect-square rounded-lg bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center mb-4 overflow-hidden">
                    {playlist.cover_url ? (
                      <img src={playlist.cover_url} alt={playlist.name} className="w-full h-full object-cover" />
                    ) : (
                      <Music className="w-16 h-16 text-muted-foreground group-hover:text-primary transition-colors" />
                    )}
                  </div>

                  {/* Info */}
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      {playlist.is_public ? (
                        <Globe className="w-3 h-3 text-muted-foreground" />
                      ) : (
                        <Lock className="w-3 h-3 text-muted-foreground" />
                      )}
                      <h3 className="font-semibold truncate">{playlist.name}</h3>
                    </div>
                    <p className="text-sm text-muted-foreground flex items-center gap-2">
                      <User className="w-3 h-3" />
                      {playlist.owner_name}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {playlist.track_count} tracks
                    </p>
                  </div>
                </Card>
              </Link>
            ))}
          </div>
        )}

        {/* Create Playlist Modal */}
        <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create New Playlist</DialogTitle>
              <DialogDescription>
                Create a playlist to organize your favorite tracks
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 mt-4">
              <div>
                <Label htmlFor="playlist-name">Name *</Label>
                <Input
                  id="playlist-name"
                  value={newPlaylist.name}
                  onChange={(e) => setNewPlaylist(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="My Awesome Playlist"
                  className="mt-1"
                  data-testid="new-playlist-name"
                />
              </div>
              
              <div>
                <Label htmlFor="playlist-description">Description</Label>
                <Textarea
                  id="playlist-description"
                  value={newPlaylist.description}
                  onChange={(e) => setNewPlaylist(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="What's this playlist about?"
                  className="mt-1"
                  rows={3}
                  data-testid="new-playlist-description"
                />
              </div>
              
              <div className="flex items-center gap-2">
                <Switch
                  id="playlist-public"
                  checked={newPlaylist.is_public}
                  onCheckedChange={(checked) => setNewPlaylist(prev => ({ ...prev, is_public: checked }))}
                />
                <Label htmlFor="playlist-public" className="flex items-center gap-2">
                  {newPlaylist.is_public ? <Globe className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
                  {newPlaylist.is_public ? 'Public playlist' : 'Private playlist'}
                </Label>
              </div>
              
              <div className="flex gap-2 pt-4">
                <Button 
                  onClick={handleCreatePlaylist} 
                  disabled={creating || !newPlaylist.name.trim()}
                  className="flex-1"
                  data-testid="confirm-create-playlist"
                >
                  {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                  Create Playlist
                </Button>
                <Button variant="outline" onClick={() => setShowCreateModal(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
